import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import {
  LayoutDashboard, ShoppingCart, Package, Users, Wallet, Layers,
  Sun, Moon, Globe, LogOut, Plus, Search, Printer, Truck, DollarSign,
  X, CheckCircle2, Send, Trash2, Pencil, Filter, FileDown, ChevronRight, ChevronDown, ChevronLeft,
  AlertTriangle, TrendingUp, Boxes, Receipt, Settings, MessageCircle,
  Sparkles, Grid, Ruler, FileText, Factory, Upload, Bell, CreditCard, ClipboardList, RefreshCw,
  ChevronsLeft, ChevronsRight, Home, Mail, Share2, Eye, EyeOff, Check, CalendarDays, Camera, FileSignature, Target, MapPin, Tag, Palette, AlertCircle, Download,
  Brain, Zap, Cpu, Info, Building2, History, Phone, Shield, BarChart2, Bot, Radio, Copy, Barcode, PlusCircle, ShieldAlert, ExternalLink, Loader2, Image as ImageIcon, Archive, Scissors,
} from "lucide-react";
import { AuthCtx, UICtx, StockSettingsCtx, RatesCtx } from "../../appContexts";
import { productEn } from "../../i18n";
import logo from "../../assets/zen-logo.png";
import api from "../../api";

const ROLE_LABELS_TR = { admin: "Yönetici", sales: "Satış", production: "Üretim", dealer: "Bayi" };

// ============ HEADER + SIDEBAR ============
function Header({ sidebarOpen = false }) {
  const { user, logout } = useContext(AuthCtx);
  const { theme, toggleTheme, lang, toggleLang, t, tr } = useContext(UICtx);
  const { rates } = useContext(RatesCtx);
  const [dealerNotifCount, setDealerNotifCount] = useState(0);
  const [waStatus, setWaStatus] = useState(null); // null | "authorized" | "disconnected" | "unconfigured"

  const fmt = (v) => v ? Number(v).toFixed(2) : "—";

  // Admin için: admin onayı bekleyen bayi siparişlerini say
  useEffect(() => {
    if (user?.role !== "admin") return;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/orders/unreviewed-count");
        setDealerNotifCount(data.count || 0);
      } catch { /* sessiz */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60 * 1000);
    return () => clearInterval(t);
  }, [user?.role]);

  // WhatsApp / Green API bağlantı durumu (3 dk'da bir kontrol)
  useEffect(() => {
    if (user?.role !== "admin") return;
    const fetchWaStatus = async () => {
      try {
        const { data } = await api.get("/whatsapp/inbox/connection-status");
        if (!data.configured) setWaStatus("unconfigured");
        else setWaStatus(data.authorized ? "authorized" : "disconnected");
      } catch { setWaStatus("disconnected"); }
    };
    fetchWaStatus();
    const t = setInterval(fetchWaStatus, 180 * 1000);
    return () => clearInterval(t);
  }, [user?.role]);

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 dark:border-[#22304A] bg-white/90 dark:bg-[#0C1220]/90 backdrop-blur-md">
      <div
        className="flex items-center justify-between pl-[76px] pr-3 md:pl-[var(--zen-header-left)] md:pr-6 h-16 transition-[padding-left] duration-300 ease-in-out"
        style={{ "--zen-header-left": sidebarOpen ? "236px" : "76px" }}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0 h-10 flex items-center justify-center rounded-xl" style={{ padding: "0 10px", overflow: "visible" }}>
            <span className="pointer-events-none absolute inset-0 rounded-xl" style={{ background: "radial-gradient(ellipse at 35% 28%, #F8FAFC 0%, #E2E8F0 22%, #94A3B8 46%, #CBD5E1 66%, #64748B 100%)", boxShadow: "inset 0 2px 5px rgba(255,255,255,0.85), inset 0 -3px 7px rgba(0,0,0,0.35), 0 6px 18px rgba(0,0,0,0.4)" }} />
            <span className="pointer-events-none absolute inset-0 rounded-xl" style={{ boxShadow: "0 0 22px 4px rgba(34,211,238,0.35), 0 0 50px 12px rgba(168,85,247,0.18)" }} />
            <img src={logo} alt="ZEN" className="h-7 relative z-10" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))" }} />
          </div>
          <div className="hidden md:block h-8 w-px bg-slate-200 dark:bg-[#22304A]" />
          <div className="hidden md:block">
            <div className="text-sm font-bold tracking-tight">{tr("ERP Yönetim Paneli")}</div>
          </div>
          {/* Döviz Kur Göstergesi */}
          {rates && (
            <div className="hidden lg:flex items-center gap-3 ml-2 pl-3 border-l border-slate-200 dark:border-[#22304A]" data-testid="header-rates-bar">
              {/* EUR */}
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="font-bold text-slate-400 dark:text-slate-500">€</span>
                <div className="flex flex-col leading-[1.1]">
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-[10px]">
                    A: {fmt(rates.eurBuy)}
                  </span>
                  <span className="font-semibold text-[#E5322A] text-[10px]">
                    S: {fmt(rates.eurSell)}
                  </span>
                </div>
              </div>
              <div className="h-4 w-px bg-slate-200 dark:bg-[#22304A]" />
              {/* USD */}
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="font-bold text-slate-400 dark:text-slate-500">$</span>
                <div className="flex flex-col leading-[1.1]">
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-[10px]">
                    A: {fmt(rates.usdBuy)}
                  </span>
                  <span className="font-semibold text-[#E5322A] text-[10px]">
                    S: {fmt(rates.usdSell)}
                  </span>
                </div>
              </div>
              {rates.tcmbDate && (
                <span className="text-[9px] text-slate-400 dark:text-slate-600 hidden xl:block">{rates.tcmbDate}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Uygulama dili (TR/EN) geçici olarak kaldırıldı — çeviri kapsamı tamamlanınca geri gelir.
              NOT: Teklif baskı dili (quotes.jsx) AYRIDIR ve müşteriler için İngilizce destekler — o korunur. */}
          <button data-testid="theme-toggle" onClick={toggleTheme} className="zen-btn-ghost !px-2.5">
            {theme === "dark"
              ? <Sun className="w-4 h-4 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]" />
              : <Moon className="w-4 h-4" />}
          </button>
          {/* WhatsApp Bağlantı Durumu — sadece admin için */}
          {user?.role === "admin" && waStatus && waStatus !== "unconfigured" && (
            <div
              data-testid="wa-connection-badge"
              title={waStatus === "authorized" ? "WhatsApp Bağlı (Green API)" : "WhatsApp Bağlantı Kesik!"}
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg border transition-colors"
              style={{
                borderColor: waStatus === "authorized" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)",
                background: waStatus === "authorized" ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
              }}
            >
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${waStatus === "authorized" ? "bg-emerald-500 animate-pulse" : "bg-rose-500 animate-bounce"}`}
              />
              <span
                className={`text-[10px] font-bold hidden sm:inline ${waStatus === "authorized" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}
              >
                WA
              </span>
            </div>
          )}
          {/* Bayi Bildirimleri — sadece admin için */}
          {user?.role === "admin" && dealerNotifCount > 0 && (
            <div className="relative" data-testid="dealer-notif-bell">
              <button
                className="zen-btn-ghost !px-2.5 relative"
                title={`${dealerNotifCount} bayi siparişi inceleme bekliyor`}
                onClick={() => { /* Siparişler sekmesine navigate için global setTab kullanılabilir */ }}
              >
                <Bell className="w-4 h-4 text-amber-500 animate-pulse" />
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#E5322A] text-white text-[9px] font-extrabold flex items-center justify-center">
                  {dealerNotifCount > 9 ? "9+" : dealerNotifCount}
                </span>
              </button>
            </div>
          )}
          <div className="hidden md:flex items-center gap-2 px-3 h-9 rounded-lg border border-slate-200 dark:border-[#22304A]">
            <div className="w-7 h-7 rounded-md bg-[#C42821] text-white flex items-center justify-center text-xs font-bold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="leading-tight">
              <div className="text-xs font-semibold">{user?.name}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{ROLE_LABELS_TR[user?.role] || user?.role}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="zen-btn-ghost">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}

// ============ SIDEBAR ============
// Fixed narrow rail on left. Always 64px wide. Hover shows a small text tooltip
// to the right of each icon. Four groups:
// Sipariş Girişi · Cari Takip · Fiyat Listeleri · Ayarlar
// macOS/iOS app-icon renk paleti — Sidebar VE modül başlıkları AYNI paleti paylaşır (tutarlı "desktop" hissi)
const MODULE_GRADIENTS = {
  dashboard:  "from-[#7FB3D8] via-[#3F7CA8] to-[#22506E]",
  orders:     "from-[#8E97E6] via-[#525CBB] to-[#2F3576]",
  quotes:     "from-[#8FB8C6] via-[#4F8394] to-[#2C5561]",
  invoices:   "from-[#A2A5D6] via-[#6A6EA8] to-[#3E4166]",
  checks:     "from-[#9BB0C2] via-[#5E7488] to-[#374757]",
  customers:  "from-[#94A9DE] via-[#5468A8] to-[#2F3E6B]",
  accounting: "from-[#B2B6C2] via-[#787D8C] to-[#474C59]",
  stock:      "from-[#9AACB6] via-[#5C6F7C] to-[#35454F]",
  pricelists: "from-[#A0B3D6] via-[#5F72A3] to-[#374766]",
  reports:    "from-[#8FC0CC] via-[#4B8B9A] to-[#2A5A66]",
  settings:   "from-[#AEB4BF] via-[#6E7683] to-[#414852]",
  crm:        "from-[#94A9DE] via-[#5468A8] to-[#2F3E6B]",
  sharemap:   "from-[#8FC0CC] via-[#4B8B9A] to-[#2A5A66]",
};

/** macOS/iOS tarzı degrade squircle ikon rozeti — modül başlıklarında, kartlarda, kısayollarda kullanılır. */
/** Ürün adı: İngilizce modda İngilizce ad + küçük puntoyla Türkçesi. */
function PName({ name, small = "text-[10px]" }) {
  const { lang } = useContext(UICtx);
  if (lang !== "en" || !name) return <>{name}</>;
  const en = productEn(name);
  return <>{en}{en !== name && <span className={`ml-1 ${small} opacity-60`}>({name})</span>}</>;
}

function ModuleIcon({ icon: Icon, tab, grad, size = "w-9 h-9", iconClass = "w-4.5 h-4.5", radius = "rounded-[11px]", className = "" }) {
  const g = grad || MODULE_GRADIENTS[tab] || MODULE_GRADIENTS.settings;
  return (
    <span className={`relative ${size} ${radius} bg-gradient-to-br ${g} flex items-center justify-center shrink-0 shadow-md shadow-black/20 ring-1 ring-black/10 ${className}`}>
      <span className={`pointer-events-none absolute inset-0 ${radius} bg-gradient-to-b from-white/28 via-white/8 to-transparent`} />
      <Icon className={`relative ${iconClass} text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]`} strokeWidth={2} />
    </span>
  );
}

// ─── Sidebar grup & alt-öğe tanımları ──────────────────────────────────────
const SIDEBAR_GROUPS = [
  {
    id: "siparisler", label: "Sipariş Yönetimi", icon: ShoppingCart, grad: MODULE_GRADIENTS.orders,
    roles: ["admin","sales","production","dealer"],
    items: [
      { tabId: "quotes",          subKey: null,        label: "Verilen Teklifler",  icon: FileSignature, roles: ["admin","sales","dealer"] },
      { tabId: "orders",          subKey: null,        label: "Satış Siparişleri",  icon: ShoppingCart,  roles: ["admin","sales","production","dealer"] },
      { tabId: "purchase_orders", subKey: null,        label: "Alış Siparişleri",   icon: Package,       roles: ["admin","sales"] },
      { tabId: "archive",         subKey: null,        label: "Arşiv",              icon: Archive,       roles: ["admin","sales","production"] },
      { tabId: "label_print",     subKey: null,        label: "Etiket Yazdır",      icon: Tag,           roles: ["admin","sales","production"] },
    ],
  },
  {
    id: "cari", label: "Cari Hesaplar", icon: Users, grad: MODULE_GRADIENTS.customers,
    roles: ["admin","sales","dealer"],
    items: [
      { tabId: "customers", subKey: "musteri",   label: "Müşteriler",    icon: Users,    roles: ["admin","sales","dealer"] },
      { tabId: "customers", subKey: "tedarikci", label: "Tedarikçiler",  icon: Truck,    roles: ["admin","sales"] },
      { tabId: "customers", subKey: "calisan",   label: "Çalışanlar",    icon: Building2,roles: ["admin"] },
      { tabId: "customers", subKey: "gruplar",   label: "Cari Grupları", icon: Grid,     roles: ["admin","sales"] },
    ],
  },
  {
    id: "fatura", label: "Fatura & İrsaliye", icon: Receipt, grad: MODULE_GRADIENTS.invoices,
    roles: ["admin","sales"],
    items: [
      { tabId: "invoices", subKey: "satis", label: "Satış Faturaları", icon: Receipt,   roles: ["admin","sales"] },
      { tabId: "invoices", subKey: "alis",  label: "Alış Faturaları",  icon: FileDown,  roles: ["admin","sales"] },
      { tabId: "invoices", subKey: "iade",  label: "İade Faturaları",  icon: RefreshCw, roles: ["admin","sales"] },
    ],
  },
  {
    id: "checks", label: "Çeki Listesi", icon: ClipboardList, grad: MODULE_GRADIENTS.checks,
    roles: ["admin","sales","production"],
    items: [
      { tabId: "checks", subKey: null, label: "Çeki Listesi", icon: ClipboardList, roles: ["admin","sales","production"] },
    ],
  },
  {
    id: "stok", label: "Stok Yönetimi", icon: Boxes, grad: MODULE_GRADIENTS.stock,
    roles: ["admin","sales"],
    items: [
      { tabId: "catalog_hierarchy", subKey: null, label: "Kartela Hiyerarşisi", icon: Palette, roles: ["admin","sales"] },
      { tabId: "stock",             subKey: null, label: "Stok Takibi",         icon: Boxes,   roles: ["admin","sales"] },
      { tabId: "pricelists",        subKey: null, label: "Fiyat Listeleri",     icon: Layers,  roles: ["admin","sales","dealer"] },
      { tabId: "label_print",       subKey: null, label: "Etiket Basma",        icon: Tag,     roles: ["admin","sales","production"] },
    ],
  },
  {
    id: "finans", label: "Finans", icon: Wallet, grad: MODULE_GRADIENTS.accounting,
    roles: ["admin","sales"],
    items: [
      { tabId: "accounting", subKey: null, label: "Muhasebe & Ödemeler", icon: Wallet, roles: ["admin","sales"] },
    ],
  },
  {
    id: "reports", label: "Raporlar", icon: TrendingUp, grad: MODULE_GRADIENTS.reports,
    roles: ["admin"],
    items: [{ tabId: "reports", subKey: null, label: "Raporlar", icon: TrendingUp, roles: ["admin"] }],
  },
  {
    id: "crm", label: "CRM", icon: Target, grad: MODULE_GRADIENTS.crm,
    roles: ["admin","sales"],
    items: [{ tabId: "crm", subKey: null, label: "CRM", icon: Target, roles: ["admin","sales"] }],
  },
  {
    id: "randevular", label: "Randevular", icon: CalendarDays, grad: "from-[#3B82F6] via-[#2563EB] to-[#1D4ED8]",
    roles: ["admin","sales","production"],
    items: [
      { tabId: "appointments", subKey: null, label: "Montaj Takvimi",      icon: CalendarDays, roles: ["admin","sales","production"] },
      { tabId: "workshops",    subKey: null, label: "Atölye & Dikiş",      icon: Scissors,     roles: ["admin","sales","production"] },
    ],
  },
  {
    id: "whatsapp_inbox", label: "WhatsApp Kutusu", icon: MessageCircle, grad: "from-[#25D366] via-[#128C7E] to-[#075E54]",
    roles: ["admin"],
    items: [
      { tabId: "whatsapp_inbox", subKey: null, label: "Gelen Kutusu", icon: MessageCircle, roles: ["admin"] },
    ],
  },
  {
    id: "settings", label: "Ayarlar", icon: Settings, grad: MODULE_GRADIENTS.settings,
    roles: ["admin","sales","production"],
    items: [
      { tabId: "settings", subKey: null, label: "Ayarlar", icon: Settings, roles: ["admin","sales","production"] },
      { tabId: "bayiloji_history", subKey: null, label: "Bayiloji Aktarım Geçmişi", icon: History, roles: ["admin"] },
      { tabId: "animasyon_demo", subKey: null, label: "Animasyon Demo", icon: Sparkles, roles: ["admin","sales","production"] },
    ],
  },
];

function Sidebar({ tab, setTab, subTab, setSubTab, sidebarOpen, setSidebarOpen }) {
  const { user } = useContext(AuthCtx);
  const { theme, tr } = useContext(UICtx);
  const [expanded, setExpanded] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("zenExpandedGroups") || "[]")); } catch { return new Set(["siparisler"]); }
  });
  const [flyout, setFlyout] = useState(null); // { groupId, top }
  const flyoutTimer = useRef(null);

  const navigate = (tabId, sk = null) => {
    // Settings alt sekme navigasyonu — SettingsPage zaten mount edilmişse event ile bildir
    if (tabId === "settings" && sk) {
      window.__zenSettingsInit = { tab: sk };
      window.dispatchEvent(new Event("zenSettingsNav"));
    }
    setTab(tabId);
    if (setSubTab) setSubTab(sk);
    setFlyout(null);
  };

  // Dar → Geniş moda geçince tüm alt menüleri kapat (manuel kontrol)
  const prevSidebarOpen = useRef(sidebarOpen);
  useEffect(() => {
    if (sidebarOpen && !prevSidebarOpen.current) {
      setExpanded(new Set());
      localStorage.setItem("zenExpandedGroups", "[]");
    }
    prevSidebarOpen.current = sidebarOpen;
  }, [sidebarOpen]);

  const toggleGroup = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      localStorage.setItem("zenExpandedGroups", JSON.stringify([...n]));
      return n;
    });
  };

  const openFlyout = (group, buttonEl) => {
    clearTimeout(flyoutTimer.current);
    const rect = buttonEl.getBoundingClientRect();
    const top = Math.min(rect.top, window.innerHeight - 280);
    setFlyout({ groupId: group.id, top });
  };

  const closeFlyout = (delay = 120) => {
    flyoutTimer.current = setTimeout(() => setFlyout(null), delay);
  };

  const keepFlyout = () => clearTimeout(flyoutTimer.current);

  const groups = SIDEBAR_GROUPS.filter(g => g.roles.includes(user?.role));
  const activeFlyoutGroup = flyout ? SIDEBAR_GROUPS.find(g => g.id === flyout.groupId) : null;

  return (
    <>
      {/* Spacer — ana içeriği kaydırır */}
      <div className="shrink-0 transition-all duration-300" style={{ width: sidebarOpen ? 224 : 64 }} aria-hidden="true" />

      {/* Fixed sidebar */}
      <aside
        data-testid="zen-sidebar"
        className={`flex fixed top-0 left-0 h-screen z-40 flex-col
                   bg-white dark:bg-[#0F1626] border-r border-slate-200 dark:border-[#22304A]
                   shadow-[6px_0_24px_-14px_rgba(0,0,0,0.6)]
                   transition-all duration-300 ease-in-out overflow-hidden`}
        style={{ width: sidebarOpen ? 224 : 64 }}
      >
        {/* Logo → Dashboard butonu */}
        <div className="h-16 flex items-center border-b border-slate-200 dark:border-[#22304A] shrink-0 px-2.5 gap-2.5">
          <button
            data-testid="nav-dashboard"
            onClick={() => navigate("dashboard")}
            aria-label="Ana Sayfa"
            title="Dashboard"
            className={`relative flex-shrink-0 w-10 h-10 rounded-[12px] flex items-center justify-center overflow-hidden
                        bg-gradient-to-br ${MODULE_GRADIENTS.dashboard} ring-1 ring-black/10
                        hover:scale-105 active:scale-95 transition-transform duration-150 cursor-pointer
                        ${tab === "dashboard" ? "scale-110 shadow-lg shadow-black/40" : "shadow-md shadow-black/25"}`}
          >
            <span className="pointer-events-none absolute inset-0 rounded-[12px] bg-gradient-to-b from-white/32 via-white/8 to-transparent" />
            <Home className="relative w-5 h-5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" strokeWidth={2} />
          </button>
          {sidebarOpen && (
            <div className="overflow-hidden cursor-pointer" onClick={() => navigate("dashboard")}>
              <div className="text-sm font-black text-[#E5322A] leading-none tracking-tight whitespace-nowrap">ZEN ERP</div>
              <div className="text-[10px] text-slate-400 dark:text-slate-300 whitespace-nowrap mt-0.5">Dashboard</div>
            </div>
          )}
        </div>

        {/* Navigasyon */}
        <nav className="zen-rail flex-1 min-h-0 py-2 flex flex-col items-center overflow-y-auto overflow-x-hidden">
          {groups.map(group => {
            const GIcon = group.icon;
            const isSingle = group.items.length === 1;
            const isGroupActive = group.items.some(it =>
              it.tabId === tab && (it.subKey == null || it.subKey === subTab)
            ) || group.items.some(it => it.tabId === tab && it.subKey == null);
            const isExp = expanded.has(group.id);
            const visibleItems = group.items.filter(it => it.roles.includes(user?.role));

            if (sidebarOpen) {
              // ── GENIŞ MOD: collapsible gruplar ─────────────────────────
              return (
                <div key={group.id} className="w-full px-2.5 mb-0.5">
                  <button
                    data-testid={`group-${group.id}`}
                    onClick={() => {
                      if (isSingle) navigate(group.items[0].tabId, group.items[0].subKey || null);
                      else toggleGroup(group.id);
                    }}
                    className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all duration-150
                                ${isGroupActive ? "bg-slate-100 dark:bg-[#1A2640]" : "hover:bg-slate-50 dark:hover:bg-[#151E30] opacity-80 hover:opacity-100"}`}
                  >
                    <div className={`relative flex-shrink-0 w-9 h-9 rounded-[11px] flex items-center justify-center overflow-hidden
                                     bg-gradient-to-br ${group.grad} ring-1 ring-black/10
                                     transition-all duration-200 ${isGroupActive ? "scale-105 shadow-md shadow-black/35" : "shadow-sm"}`}>
                      <span className="pointer-events-none absolute inset-0 rounded-[11px] bg-gradient-to-b from-white/28 via-white/8 to-transparent" />
                      <GIcon className="relative text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" strokeWidth={2} style={{ width: 18, height: 18 }} />
                    </div>
                    <span className={`text-[12.5px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis flex-1
                                      ${isGroupActive ? "text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-300"}`}>
                      {group.label}
                    </span>
                    {!isSingle
                      ? <ChevronRight className={`w-3.5 h-3.5 text-slate-400 dark:text-slate-300 transition-transform duration-200 ${isExp ? "rotate-90" : ""}`} />
                      : isGroupActive && <div className="w-1.5 h-1.5 rounded-full bg-[#E5322A]" />}
                  </button>

                  {!isSingle && isExp && (
                    <div className="mt-0.5 ml-4 border-l border-slate-200 dark:border-[#22304A] pl-2 space-y-0.5 pb-1">
                      {visibleItems.map(item => {
                        const IIcon = item.icon;
                        const isActive = item.tabId === tab && (item.subKey == null ? !subTab || !group.items.some(i => i.subKey === subTab && i.tabId === tab) : item.subKey === subTab);
                        return (
                          <button
                            key={`${item.tabId}-${item.subKey || "main"}`}
                            data-testid={`nav-${item.tabId}${item.subKey ? `-${item.subKey}` : ""}`}
                            onClick={() => navigate(item.tabId, item.subKey || null)}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11.5px] font-medium transition-all duration-150
                                        ${isActive
                                          ? "bg-[#E5322A]/10 text-[#C42821] dark:text-[#ff6b6b] font-semibold"
                                          : "text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#151E30] hover:text-slate-700 dark:hover:text-slate-100"}`}
                          >
                            <IIcon className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-[#E5322A]" : "opacity-60"}`} strokeWidth={2} />
                            {item.label}
                            {isActive && <div className="ml-auto w-1 h-1 rounded-full bg-[#E5322A]" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // ── DAR MOD: tek ikon + flyout/tooltip hover ───────────────────────
            const isFlyoutOpen = flyout?.groupId === group.id;
            return (
              <div key={group.id} className="relative w-full flex flex-col items-center">
                <button
                  data-testid={`group-${group.id}`}
                  onClick={(e) => {
                    if (isSingle) {
                      navigate(group.items[0].tabId, group.items[0].subKey || null);
                    } else {
                      isFlyoutOpen ? setFlyout(null) : openFlyout(group, e.currentTarget);
                    }
                  }}
                  onMouseEnter={(e) => { keepFlyout(); openFlyout(group, e.currentTarget); }}
                  onMouseLeave={() => closeFlyout(isSingle ? 80 : 120)}
                  aria-label={group.label}
                  className={`zen-rail-item relative w-11 h-11 rounded-[13px] flex items-center justify-center shrink-0
                              bg-gradient-to-br ${group.grad} ring-1 ring-black/10
                              transition-all duration-200 ease-out
                              ${isGroupActive || isFlyoutOpen
                                ? "scale-110 shadow-lg shadow-black/40"
                                : "scale-100 opacity-85 shadow-md shadow-black/25 hover:opacity-100 hover:scale-105 hover:shadow-lg hover:shadow-black/30"}`}
                >
                  <span className="pointer-events-none absolute inset-0 rounded-[13px] bg-gradient-to-b from-white/28 via-white/8 to-transparent" />
                  <span className="pointer-events-none absolute inset-0 rounded-[13px] shadow-[inset_0_-2px_3px_rgba(0,0,0,0.25)]" />
                  <GIcon className="relative w-5 h-5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" strokeWidth={2} />
                </button>
                <span className={`mt-1 w-1 h-1 rounded-full bg-slate-800 dark:bg-white transition-opacity duration-200 ${isGroupActive ? "opacity-70" : "opacity-0"}`} />
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-slate-200 dark:border-[#22304A] shrink-0">
          {sidebarOpen && user && (
            <div className="px-3 py-2.5 flex items-center gap-2.5" data-testid="sidebar-user-card">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-[11px] font-bold select-none ring-2 ring-[#E5322A]/30"
                   style={{ background: "linear-gradient(135deg,#E5322A 0%,#a52020 100%)" }}>
                {(user.name || user.email || "Z").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <div className="text-[11.5px] font-bold text-slate-800 dark:text-white truncate leading-tight">
                  {user.name || user.email?.split("@")[0] || "Kullanıcı"}
                </div>
                <div className="text-[9.5px] font-semibold text-slate-400 dark:text-slate-300 uppercase tracking-wider leading-tight mt-0.5">
                  {ROLE_LABELS_TR[user.role] || user.role}
                </div>
              </div>
            </div>
          )}
          <div className="py-2 flex justify-center">
            {sidebarOpen ? (
              <button
                data-testid="sidebar-collapse-btn"
                onClick={() => { setSidebarOpen(false); localStorage.setItem("zenSidebarOpen", "false"); }}
                className="flex items-center gap-2 w-full mx-2.5 px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-[#1A2640] text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-100 transition-colors text-xs font-semibold"
              >
                <ChevronsLeft className="w-4 h-4" />
                <span>Daralt</span>
              </button>
            ) : (
              <button
                data-testid="sidebar-expand-btn"
                onClick={() => { setSidebarOpen(true); localStorage.setItem("zenSidebarOpen", "true"); }}
                className="w-9 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 dark:hover:bg-[#1A2640] text-slate-400 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-100 transition-colors"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* ── LIQUID GLASS FLYOUT — dar modda aside DIŞINDA render ── */}
      {!sidebarOpen && activeFlyoutGroup && (
        <div
          data-testid="sidebar-flyout"
          className="fixed z-50"
          style={{ left: 68, top: flyout.top }}
          onMouseEnter={keepFlyout}
          onMouseLeave={() => closeFlyout()}
        >
          {/* ── TEK ÖĞE: kompakt tooltip balonu ── */}
          {activeFlyoutGroup.items.filter(it => it.roles.includes(user?.role)).length <= 1 ? (
            <button
              className="pointer-events-auto ml-2 flex items-center gap-2 px-3.5 py-2 rounded-xl
                         text-white text-[12px] font-semibold whitespace-nowrap
                         shadow-[0_4px_20px_rgba(0,0,0,0.35),0_1px_4px_rgba(0,0,0,0.2)]
                         border border-white/15 transition-all duration-100
                         hover:brightness-110 active:scale-95"
              style={{
                background: theme === "dark"
                  ? "linear-gradient(135deg,rgba(30,41,59,0.97) 0%,rgba(15,23,42,0.97) 100%)"
                  : "linear-gradient(135deg,rgba(30,41,59,0.95) 0%,rgba(15,23,42,0.95) 100%)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              }}
              onClick={() => {
                const item = activeFlyoutGroup.items[0];
                navigate(item.tabId, item.subKey || null);
              }}
              data-testid={`tooltip-nav-${activeFlyoutGroup.id}`}
            >
              {/* Renkli ikon rozeti */}
              <div className={`relative w-5 h-5 rounded-[6px] flex items-center justify-center overflow-hidden flex-shrink-0
                               bg-gradient-to-br ${activeFlyoutGroup.grad} ring-1 ring-black/20 shadow-sm`}>
                <span className="pointer-events-none absolute inset-0 rounded-[6px] bg-gradient-to-b from-white/30 to-transparent" />
                {React.createElement(activeFlyoutGroup.icon, {
                  className: "relative text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]",
                  strokeWidth: 2.2,
                  style: { width: 11, height: 11 },
                })}
              </div>
              {tr(activeFlyoutGroup.items[0].label || activeFlyoutGroup.label)}
              {/* Ok işareti */}
              <span
                className="absolute right-full top-1/2 -translate-y-1/2"
                style={{
                  borderWidth: "5px",
                  borderStyle: "solid",
                  borderColor: "transparent",
                  borderRightColor: theme === "dark" ? "rgba(15,23,42,0.97)" : "rgba(15,23,42,0.95)",
                }}
              />
            </button>
          ) : (
            /* ── ÇOK ÖĞE: mevcut liquid glass flyout paneli ── */
            <div
              className="min-w-[188px] rounded-2xl overflow-hidden
                         border border-white/30 dark:border-white/10
                         shadow-[0_8px_32px_rgba(0,0,0,0.22),0_2px_8px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.4)]
                         dark:shadow-[0_8px_32px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.1)]"
              style={{
                background: theme === "dark" ? "rgba(18,26,44,0.92)" : "rgba(248,250,255,0.88)",
                backdropFilter: "blur(24px) saturate(200%) brightness(1.05)",
                WebkitBackdropFilter: "blur(24px) saturate(200%) brightness(1.05)",
              }}
            >
              {/* Grup başlığı */}
              <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-2">
                <div className={`relative w-7 h-7 rounded-[9px] flex items-center justify-center overflow-hidden
                                 bg-gradient-to-br ${activeFlyoutGroup.grad} ring-1 ring-black/10 shadow-sm`}>
                  <span className="pointer-events-none absolute inset-0 rounded-[9px] bg-gradient-to-b from-white/30 to-transparent" />
                  {React.createElement(activeFlyoutGroup.icon, {
                    className: "relative text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]",
                    strokeWidth: 2,
                    style: { width: 15, height: 15 },
                  })}
                </div>
                <span className="text-[11px] font-black tracking-wide text-slate-700 dark:text-slate-200 uppercase opacity-70">
                  {activeFlyoutGroup.label}
                </span>
              </div>

              {/* Ayırıcı */}
              <div className="mx-3 mb-1.5 h-px bg-gradient-to-r from-transparent via-slate-300/60 dark:via-white/10 to-transparent" />

              {/* Alt öğeler */}
              <div className="px-2 pb-2.5 space-y-0.5">
                {activeFlyoutGroup.items
                  .filter(it => it.roles.includes(user?.role))
                  .map(item => {
                    const IIcon = item.icon;
                    const isActive = item.tabId === tab && (item.subKey == null || item.subKey === subTab);
                    return (
                      <button
                        key={`${item.tabId}-${item.subKey || "main"}`}
                        data-testid={`flyout-nav-${item.tabId}${item.subKey ? `-${item.subKey}` : ""}`}
                        onClick={() => navigate(item.tabId, item.subKey || null)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-medium transition-all duration-100
                                    ${isActive
                                      ? "bg-[#E5322A]/12 text-[#C42821] font-semibold"
                                      : "text-slate-600 dark:text-slate-300 hover:bg-black/6 dark:hover:bg-white/8 hover:text-slate-900 dark:hover:text-white"}`}
                      >
                        <IIcon className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-[#E5322A]" : "text-slate-400 dark:text-slate-300"}`} strokeWidth={2} />
                        <span>{item.label}</span>
                        {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#E5322A]" />}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Küçük ok — çok öğeli flyout için */}
          {activeFlyoutGroup.items.filter(it => it.roles.includes(user?.role)).length > 1 && (
            <div
              className="absolute -left-1.5 border-t border-l border-white/30 dark:border-white/10"
              style={{
                top: "24px",
                width: 10, height: 10,
                background: theme === "dark" ? "rgba(18,26,44,0.92)" : "rgba(248,250,255,0.88)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                transform: "rotate(-45deg)",
                boxShadow: "-2px -2px 4px rgba(0,0,0,0.06)",
              }}
            />
          )}
        </div>
      )}
    </>
  );
}

// Ana sayfa: bugünkü + gecikmiş CRM takipleri hatırlatma kutusu

export { Header, Sidebar, ModuleIcon, PName, MODULE_GRADIENTS, ROLE_LABELS_TR };
export default Sidebar;
